import { type ChangeEvent, type FormEvent, useEffect, useState } from "react";
import { Avatar } from "../components/Avatar";
import { useMe, useUpdateProfile } from "../lib/api";
import { useAuthStore } from "../lib/auth-store";
import { uploadToStorage } from "../lib/storage";
import { validateImage, validateNickname } from "../lib/validators";

export function Profile() {
  const fbUser = useAuthStore((s) => s.fbUser);
  const { data: me } = useMe(Boolean(fbUser));
  const update = useUpdateProfile();

  const [nickname, setNickname] = useState("");
  const [bio, setBio] = useState("");
  const [avatar, setAvatar] = useState<{ url: string | null; key: string | null }>({
    url: null,
    key: null,
  });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (me) {
      setNickname(me.nickname);
      setBio(me.bio);
      setAvatar({ url: me.avatarUrl, key: me.avatarKey });
    }
  }, [me]);

  async function onAvatarPick(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const err = validateImage(file);
    if (err) {
      setError(err);
      return;
    }
    setError(null);
    try {
      const stored = await uploadToStorage(file);
      setAvatar({ url: URL.createObjectURL(file), key: stored.storagePath });
    } catch (uploadErr) {
      setError(uploadErr instanceof Error ? uploadErr.message : "이미지 업로드 실패");
    }
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    const err = validateNickname(nickname);
    if (err) {
      setError(err);
      return;
    }
    setError(null);
    update.mutate({ nickname, bio, avatarKey: avatar.key });
  }

  if (!me) {
    return <div className="mt-20 text-center font-mono text-sm text-cyan-300/70">불러오는 중…</div>;
  }

  return (
    <section className="mx-auto max-w-lg fade-up">
      <div className="glass neon-border rounded-2xl p-7">
        <h1 className="neon-text text-2xl font-bold">내 프로필</h1>
        <p className="mt-1 text-xs text-slate-500">
          {me.email} · {me.role}
        </p>

        <form className="mt-5 flex flex-col gap-4" onSubmit={onSubmit} noValidate>
          <div className="flex items-center gap-4">
            <Avatar avatarUrl={avatar.url} nickname={nickname || me.nickname} size={64} />
            <label className="text-sm text-slate-300">
              프로필 사진
              <input
                aria-label="프로필 사진"
                type="file"
                accept="image/*"
                onChange={onAvatarPick}
                className="mt-1 block w-full text-sm text-slate-400 file:mr-3 file:rounded-lg file:border file:border-cyan-400/40 file:bg-cyan-400/10 file:px-3 file:py-1.5 file:text-cyan-200"
              />
            </label>
          </div>
          <label className="flex flex-col gap-1 text-sm text-slate-300">
            닉네임
            <input
              aria-label="닉네임"
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              className="input-cyber rounded-lg px-3 py-2.5"
            />
          </label>
          <label className="flex flex-col gap-1 text-sm text-slate-300">
            자기소개
            <textarea
              aria-label="자기소개"
              value={bio}
              rows={4}
              onChange={(e) => setBio(e.target.value)}
              className="input-cyber rounded-lg px-3 py-2.5"
            />
          </label>
          {error && <p className="text-sm text-fuchsia-400">{error}</p>}
          {update.isSuccess && <p className="text-sm text-cyan-300">저장되었습니다.</p>}
          <button
            type="submit"
            disabled={update.isPending}
            className="btn-neon rounded-lg py-2.5 font-semibold"
          >
            저장
          </button>
        </form>
      </div>
    </section>
  );
}
